import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscEdit, VscTrash, VscCheck, VscClose } from 'react-icons/vsc';
import { RiFolderLine, RiFolderUserLine } from 'react-icons/ri';
import { getFolders, createFolder, renameFolder, deleteFolder } from '../../api/projectFiles';
import { getPersonalFolders, createPersonalFolder, renamePersonalFolder, deletePersonalFolder } from '../../api/personalFiles';
import './FileManager.css';

const DEFAULT_FOLDERS = [
  'Mechanical drawings',
  'Electrical drawings',
  'Hydraulic drawings',
  'Arrangement drawings',
  'Specification',
  'Meetings',
];

function FolderItem({ folder, tabId, isActive, canEdit, onOpen, onDblClick, onRename, onDelete, color }) {
  const [editing, setEditing] = useState(false);
  const [name, setName]       = useState(folder.name);

  const commit = async () => {
    if (name.trim() && name.trim() !== folder.name) await onRename(folder, name.trim());
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="filesb-edit-row">
        <input className="filesb-name-input" value={name} autoFocus
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setName(folder.name); setEditing(false); } }} />
        <button className="fl-btn-icon fl-btn-save" onClick={commit}><VscCheck size={13} /></button>
        <button className="fl-btn-icon" onClick={() => { setName(folder.name); setEditing(false); }}><VscClose size={13} /></button>
      </div>
    );
  }
  return (
    <div className={`sp-nav-item filesb-item${isActive ? ' active' : ''}`}
      onClick={onOpen} onDoubleClick={onDblClick}>
      <RiFolderLine size={14} style={{ flexShrink: 0, color }} />
      <span className="filesb-item-name">{folder.name}</span>
      {canEdit && (
        <div className="filesb-item-actions">
          <button className="fl-btn-icon" title="Rename"
            onClick={e => { e.stopPropagation(); setEditing(true); }}>
            <VscEdit size={12} />
          </button>
          <button className="fl-btn-icon fl-btn-del" title="Delete"
            onClick={e => { e.stopPropagation(); onDelete(folder); }}>
            <VscTrash size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function FileSidebar({ project, activeTabId, onOpenFolder, onPreviewFolder, onOpenPersonalFolder, onPreviewPersonalFolder, role }) {
  // Project folders
  const [folders, setFolders]       = useState([]);
  const [adding, setAdding]         = useState(false);
  const [newName, setNewName]       = useState('');
  const timers = useRef({});

  const canEdit = ['admin','engineer','manager','foreman'].includes(role);

  useEffect(() => {
    if (!project) return;
    getFolders(project._id).then(async (data) => {
      if (data.length === 0 && canEdit) {
        const created = await Promise.all(DEFAULT_FOLDERS.map(n => createFolder(project._id, n)));
        setFolders(created);
      } else {
        setFolders(data);
      }
    }).catch(() => {});
  }, [project?._id]);

  const handleAdd = async () => {
    if (!newName.trim() || !project) return;
    const folder = await createFolder(project._id, newName.trim());
    setFolders(prev => [...prev, folder]);
    setAdding(false); setNewName('');
    onOpenFolder(folder);
  };

  const handleRename = async (folder, name) => {
    const updated = await renameFolder(folder._id, name);
    setFolders(prev => prev.map(f => f._id === folder._id ? updated : f));
  };

  const handleDelete = async (folder) => {
    if (!confirm(`Delete "${folder.name}" and all its files?`)) return;
    await deleteFolder(folder._id);
    setFolders(prev => prev.filter(f => f._id !== folder._id));
  };

  const clickFolder = (folder) => {
    const id = `files-folder-${folder._id}`;
    if (timers.current[id]) return;
    timers.current[id] = setTimeout(() => {
      delete timers.current[id];
      onPreviewFolder ? onPreviewFolder(folder) : onOpenFolder(folder);
    }, 220);
  };
  const dblClickFolder = (folder) => {
    const id = `files-folder-${folder._id}`;
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    onOpenFolder(folder);
  };

  // Personal folders
  const [pFolders, setPFolders]     = useState([]);
  const [pAdding, setPAdding]       = useState(false);
  const [pNewName, setPNewName]     = useState('');

  useEffect(() => {
    getPersonalFolders().then(data => setPFolders(data)).catch(() => {});
  }, []);

  const handlePAdd = async () => {
    if (!pNewName.trim()) return;
    const folder = await createPersonalFolder(pNewName.trim());
    setPFolders(prev => [...prev, folder]);
    setPAdding(false); setPNewName('');
    onOpenPersonalFolder?.(folder);
  };

  const handlePRename = async (folder, name) => {
    const updated = await renamePersonalFolder(folder._id, name);
    setPFolders(prev => prev.map(f => f._id === folder._id ? updated : f));
  };

  const handlePDelete = async (folder) => {
    if (!confirm(`Delete "${folder.name}" and all its files?`)) return;
    await deletePersonalFolder(folder._id);
    setPFolders(prev => prev.filter(f => f._id !== folder._id));
  };

  const clickPFolder = (folder) => {
    const id = `personal-folder-${folder._id}`;
    if (timers.current[id]) return;
    timers.current[id] = setTimeout(() => {
      delete timers.current[id];
      onPreviewPersonalFolder ? onPreviewPersonalFolder(folder) : onOpenPersonalFolder?.(folder);
    }, 220);
  };
  const dblClickPFolder = (folder) => {
    const id = `personal-folder-${folder._id}`;
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    onOpenPersonalFolder?.(folder);
  };

  return (
    <div className="side-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Project files ── */}
      <div className="side-panel-header">
        <span className="side-panel-title">FILES</span>
        {canEdit && (
          <button className="btn-icon" title="New folder" onClick={() => setAdding(a => !a)}>
            <VscAdd size={16} />
          </button>
        )}
      </div>

      {adding && (
        <div className="filesb-add-form">
          <input className="filesb-name-input" placeholder="Folder name…" value={newName} autoFocus
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAdding(false); }} />
          <div className="filesb-add-actions">
            <button className="btn-primary" onClick={handleAdd} disabled={!newName.trim()}>Add</button>
            <button className="btn-secondary" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <nav className="sp-nav" style={{ flex: 1, overflowY: 'auto' }}>
        {folders.map(folder => (
          <FolderItem key={folder._id}
            folder={folder}
            tabId={`files-folder-${folder._id}`}
            isActive={activeTabId === `files-folder-${folder._id}`}
            canEdit={canEdit}
            onOpen={() => clickFolder(folder)}
            onDblClick={() => dblClickFolder(folder)}
            onRename={handleRename}
            onDelete={handleDelete}
            color="#e5c07b" />
        ))}
        {folders.length === 0 && !adding && (
          <div className="side-panel-empty">No folders yet.</div>
        )}
      </nav>

      {/* ── Own files ── */}
      <div className="side-panel-divider" />
      <div className="side-panel-header">
        <span className="side-panel-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <RiFolderUserLine size={13} />OWN FILES
        </span>
        <button className="btn-icon" title="New personal folder" onClick={() => setPAdding(a => !a)}>
          <VscAdd size={16} />
        </button>
      </div>

      {pAdding && (
        <div className="filesb-add-form">
          <input className="filesb-name-input" placeholder="Folder name…" value={pNewName} autoFocus
            onChange={e => setPNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handlePAdd(); if (e.key === 'Escape') setPAdding(false); }} />
          <div className="filesb-add-actions">
            <button className="btn-primary" onClick={handlePAdd} disabled={!pNewName.trim()}>Add</button>
            <button className="btn-secondary" onClick={() => setPAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <nav className="sp-nav">
        {pFolders.map(folder => (
          <FolderItem key={folder._id}
            folder={folder}
            tabId={`personal-folder-${folder._id}`}
            isActive={activeTabId === `personal-folder-${folder._id}`}
            canEdit={true}
            onOpen={() => clickPFolder(folder)}
            onDblClick={() => dblClickPFolder(folder)}
            onRename={handlePRename}
            onDelete={handlePDelete}
            color="#9cdcfe" />
        ))}
        {pFolders.length === 0 && !pAdding && (
          <div className="side-panel-empty">No personal folders.</div>
        )}
      </nav>
    </div>
  );
}
